import { useEffect, useState, useRef, useCallback } from "react";
import { ProductsTypes } from "../../../../api/types/products.types";
import ProductsService from "../../../../api/services/products.service";
import ProductCard from "../../../common/product-card/ProductCard";
import styles from "./ProductsSection.module.scss";
import Loading from "../../../common/loading/Loading";
import Button from "../../../common/button/Button";
import { useTranslation } from "react-i18next";
import { useCurrencies } from "../../../../context/CurrenciesContext";
import { useLanguage } from "../../../../context/LanguageContext";
import { groupProductVariants } from "../../../../utils/groupProductVariants";

const PRODUCTS_PER_PAGE = 50;
const INFINITE_SCROLL_LIMIT = 300;

const ProductsSection = () => {
  const [products, setProducts] = useState<ProductsTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [useInfiniteScroll, setUseInfiniteScroll] = useState(true);

  const { currencyId } = useCurrencies();
  const { t } = useTranslation("translation");
  const { currentLanguage } = useLanguage();
  const observerTarget = useRef<HTMLDivElement>(null);

  const fetchProducts = useCallback(
    async (pageNum: number, isInitial: boolean = false) => {
      if (!currencyId) return;
      isInitial ? setLoading(true) : setLoadingMore(true);

      try {
        const res = await ProductsService.getAllProducts(
          pageNum,
          PRODUCTS_PER_PAGE,
          Number(currencyId),
        );
        const variants = res?.data?.data || [];
        const totalCount = res?.data?.count || 0;

        // Group variants by product_id and select best variant for each product
        const groupedProducts = groupProductVariants(variants);

        setProducts((prev) =>
          isInitial ? groupedProducts : [...prev, ...groupedProducts],
        );
        // Calculate has_more using total count from backend
        // Stop if no new grouped products (edge case) or all variants loaded
        const itemsLoaded = pageNum * PRODUCTS_PER_PAGE;
        const noNewGroupedProducts = groupedProducts.length === 0;
        setHasMore(!noNewGroupedProducts && itemsLoaded < totalCount);
        // Only update page state for initial load (subsequent loads set it in the caller)
        if (isInitial) {
          setPage(pageNum);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [currencyId],
  );

  useEffect(() => {
    fetchProducts(1, true);
  }, [currencyId, fetchProducts]);

  useEffect(() => {
    if (products.length >= INFINITE_SCROLL_LIMIT) {
      setUseInfiniteScroll(false);
    }
  }, [products.length]);

  useEffect(() => {
    if (!useInfiniteScroll || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchProducts(nextPage);
        }
      },
      { threshold: 0.5 },
    );

    if (observerTarget.current) observer.observe(observerTarget.current);

    return () => observer.disconnect();
  }, [useInfiniteScroll, hasMore, loadingMore, loading, page, fetchProducts]);

  const getLocalizedName = (product: ProductsTypes) => {
    switch (currentLanguage) {
      case "az":
        return product.name_az || product.name_en;
      case "de":
        return product.name_de || product.name_en;
      case "es":
        return product.name_es || product.name_en;
      case "en":
      default:
        return product.name_en;
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <section className={styles.products_section}>
      <div className="container">
        <div className={styles.header}>
          <h1 className={styles.section_title}>
            {t("top_products") || "Top məhsullar"}
          </h1>
          <a href="/products" className={styles.view_all}>
            {t("view_all") || "Hamısına bax"}
          </a>
        </div>
        <div className={styles.products_list}>
          {products.map((p, i) => (
            <ProductCard
              id={p.id}
              key={p.id}
              title={getLocalizedName(p)}
              currency={p.currency_name}
              price={p.price?.toString()}
              discount_percent={p.discount_percent?.toString()}
              discount_price={p.discount_price?.toString()}
              image={p.images ? p.images[0]?.image : ""}
              stock_quantity={p.stock_quantity}
              wishlist={p.is_wish}
              images={p.images}
              attributes={p.attributes}
            />
          ))}
        </div>

        {useInfiniteScroll && hasMore && (
          <div ref={observerTarget} className={styles.scroll_trigger}>
            {loadingMore && <Loading />}
          </div>
        )}

        {!useInfiniteScroll && hasMore && (
          <div className={styles.load_more_container}>
            <Button
              onClick={() => {
                const nextPage = page + 1;
                setPage(nextPage);
                fetchProducts(nextPage);
              }}
              loading={loadingMore}
              disabled={loadingMore}
              className={styles.load_more_btn}
            >
              {t("load_more")}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;
